/**
 * MOCK · ASSUMED (CT-5724) — what History adds to the Activity feed of CT-5527:
 * task rows, a call record, the calls and meetings the user logs by hand, and
 * the From notes subtab (meeting insights and the Concierge's readings).
 *
 * The rows are the prototype's (record PER-0104), worded with the record's
 * name, and merged into the feed at query time like the notes of CT-5528.
 *
 * QA switch, read from the URL on every request (`?mockHistory=…`):
 *
 *   empty      From notes has nothing
 *   fail       From notes fails
 *   log_fail   Log call / Log meeting fail
 */

import { getMockEntityHeaders } from './entityHeader.mock.js';

export const HISTORY_SCENARIOS = Object.freeze(['empty', 'fail', 'log_fail']);

const DAY_MS = 24 * 60 * 60 * 1000;

let scenarioOverride = null;
/** Calls and meetings logged by hand, per record, newest last. */
let logged = new Map();

const recordKey = (entityType, entityId) => `${entityType}/${entityId}`;

const readScenario = () => {
  if (scenarioOverride) return scenarioOverride;
  try {
    const value = new URLSearchParams(window.location.search).get('mockHistory');
    return HISTORY_SCENARIOS.includes(value) ? value : null;
  } catch {
    return null;
  }
};

const nameOf = (entityType, entityId) => {
  const rows = getMockEntityHeaders()[entityType] ?? [];
  const row = rows.find((candidate) => (candidate.entity_id ?? candidate.entityId ?? candidate.id) === entityId);
  return row?.title ?? row?.name ?? 'this contact';
};

const daysAgo = (days, hour = 9) => {
  const date = new Date(Date.now() - days * DAY_MS);
  date.setHours(hour, 0, 0, 0);
  return date.toISOString();
};

/** The task rows and the call record History shows besides emails, SMS and notes. */
const buildRows = (name) => [
  {
    id: 'hist-task-checkin',
    kind: 'task',
    title: `Book the next check-in with ${name}`,
    occurred_at: daysAgo(20, 8),
    status: { label: 'Overdue', variant: 'error' },
    assignee: 'Priya Nair',
    due_at: daysAgo(17),
    context: 'Account renewal',
    origin: 'Next best action',
  },
  {
    id: 'hist-task-next-steps',
    kind: 'task',
    title: 'Prepare next steps from the renewal follow-up call',
    occurred_at: daysAgo(46, 10),
    status: { label: 'Completed', variant: 'success' },
    assignee: 'Concierge',
    due_at: daysAgo(46),
    context: 'Account renewal',
    origin: 'Agent',
  },
  {
    id: 'hist-task-qbr',
    kind: 'task',
    title: 'Log the QBR outcomes in HubSpot',
    occurred_at: daysAgo(57, 9),
    status: { label: 'Overdue', variant: 'error' },
    assignee: 'Priya Nair',
    due_at: daysAgo(13),
    context: 'Account renewal',
    origin: 'HubSpot sync',
  },
  {
    id: 'hist-call-renewal',
    kind: 'call',
    direction: 'outbound',
    channel: 'call',
    title: 'Renewal follow-up · Call',
    // Recent enough to sit on the Timeline's first page beside the seeded emails.
    occurred_at: daysAgo(4, 14),
    status: { label: 'Resolved', variant: 'success' },
    record_label: 'Meeting record',
    ai_summary: `${name} confirmed the evaluation is still funded and asked for a written migration timeline. No pricing objection was raised.`,
    phone: '+1 (212) 555-0155',
    duration_seconds: 378,
    agent_tools: 2,
    tone: 'Positive tone',
  },
];

/** Rows History merges into the feed for this record: the seed plus what was logged. */
export const listMockHistoryRows = (entityType, entityId) => [
  ...buildRows(nameOf(entityType, entityId)),
  ...(logged.get(recordKey(entityType, entityId)) ?? []),
];

/**
 * A call or meeting logged by hand (CT-5724, as in the prototype): what it was
 * about and how it went are required; the duration and what was said are
 * optional. The server dates it — it is logged now, about something just done.
 */
export const logMockInteraction = ({
  kind,
  entity_type: entityType,
  entity_id: entityId,
  subject,
  outcome,
  duration_seconds: durationSeconds,
  summary,
  attachments = [],
  links = [],
}) => {
  if (readScenario() === 'log_fail') {
    return { error: { status: 500, description: 'Mock logging failure (?mockHistory=log_fail).' } };
  }
  if (!['call', 'meeting'].includes(kind)) return { error: { status: 400, description: `Unknown kind ${kind}` } };
  const missing = [!String(subject ?? '').trim() && 'subject', !outcome && 'outcome'].filter(Boolean);
  if (missing.length) return { error: { status: 422, description: `Missing required values: ${missing.join(', ')}` } };

  const key = recordKey(entityType, entityId);
  const rows = logged.get(key) ?? [];
  const id = `hist-log-${rows.length + 1}`;
  rows.push({
    id,
    kind,
    channel: kind,
    title: subject.trim(),
    outcome,
    occurred_at: new Date().toISOString(),
    preview: String(summary ?? '').trim() || null,
    duration_seconds: durationSeconds ?? null,
    logged_by_me: true,
    attachments,
    links,
  });
  logged.set(key, rows);
  return { body: { id }, meta: {} };
};

// ─── From notes ──────────────────────────────────────────────────────────────

const buildNotesFeed = (name) => ({
  insights: [
    {
      id: 'ins-renewal-call',
      title: 'Call · Renewal follow-up',
      subtitle: 'Discovery call',
      unconfirmed_claims: 3,
      record_label: 'Meeting record',
      occurred_at: daysAgo(47, 14),
      file: 'Call-transcript-2026-09-02.txt',
      topics: [
        { id: 'top-1', kind: 'topic', text: 'Reports to, evaluation funding, pricing.', origin: 'Call-transcript-2026-09-02.txt' },
      ],
    },
    {
      id: 'ins-qbr',
      title: 'Quarterly business review',
      subtitle: 'QBR',
      unconfirmed_claims: 3,
      record_label: 'Meeting record',
      occurred_at: daysAgo(58, 11),
      file: 'QBR-transcript-aug26.txt',
      topics: [],
    },
  ],
  other: [
    {
      id: 'oth-1',
      kind: 'topic',
      title: 'Topics · Email thread · Migration timeline',
      text: 'Security review, also requested.',
      origin: 'Email thread · Migration timeline',
    },
    {
      id: 'oth-2',
      kind: 'topic',
      title: 'Topics · Inferred by Concierge',
      text: 'Decision role, signing authority, deciding criterion.',
      origin: 'Inferred by Concierge',
    },
    {
      id: 'oth-3',
      kind: 'interpretation',
      title: 'Patience is thinning on “Migration timeline”',
      text: `The second ask from ${name} about “Migration timeline” was shorter than the first and dropped the pleasantries. Nothing in it is hostile.`,
      origin: 'Migration timeline · Aug 18',
    },
    {
      id: 'oth-4',
      kind: 'summary',
      title: 'Summary · Governance addendum thread',
      text: 'Legal has the redlined addendum; clause 7 is the only open point.',
      origin: 'Email thread · Governance addendum',
    },
  ],
});

/** Which kinds each From notes chip keeps. Meeting insights are summaries of a record. */
const NOTES_QUICK_FILTER_KINDS = Object.freeze({
  all: ['summary', 'topic', 'interpretation'],
  summaries: ['summary'],
  topics: ['topic'],
  interpretations: ['interpretation'],
});

/** Whether a note says `query` in one of the searched fields (case-insensitive). */
const noteMatches = (note, search) => {
  const query = search?.query?.trim().toLowerCase();
  if (!query) return true;
  const fields = search.fields?.length ? search.fields : ['title', 'text', 'origin'];
  return fields.some((field) => String(note[field] ?? '').toLowerCase().includes(query));
};

export const listMockNotesFeed = ({ entityType, entityId, quickFilter = 'all', search }) => {
  const scenario = readScenario();
  if (scenario === 'fail') return { error: { status: 500, description: 'Mock notes failure (?mockHistory=fail).' } };
  if (scenario === 'empty') return { body: { insights: [], other: [] }, meta: {} };

  const kinds = NOTES_QUICK_FILTER_KINDS[quickFilter] ?? NOTES_QUICK_FILTER_KINDS.all;
  const feed = buildNotesFeed(nameOf(entityType, entityId));
  const showInsights = kinds.includes('summary') || kinds.includes('topic');

  return {
    body: {
      // A meeting record stays when it — or one of its topics — matches.
      insights: showInsights
        ? feed.insights
            .map((insight) => ({ ...insight, topics: insight.topics.filter((topic) => kinds.includes(topic.kind)) }))
            .filter((insight) => noteMatches(insight, search) || insight.topics.some((topic) => noteMatches(topic, search)))
        : [],
      other: feed.other.filter((note) => kinds.includes(note.kind) && noteMatches(note, search)),
    },
    meta: {},
  };
};

export const __setMockHistoryScenario = (value) => {
  scenarioOverride = value;
};

export const __resetMockHistoryForTests = () => {
  scenarioOverride = null;
  logged = new Map();
};
